import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Download, Copy, Check, AlertTriangle } from 'lucide-react';
import { generateDSL } from '@/utils/dslGenerator';
import { toast } from 'sonner';

const DSLViewer = ({ machine, machineId, onDSLEdit }) => {
  const [dslText, setDslText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedDSL, setEditedDSL] = useState('');
  const [copied, setCopied] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (machine) {
      const generated = generateDSL(machine);
      setDslText(generated);
      if (!isEditing) {
        setEditedDSL(generated);
      }
    }
  }, [machine, isEditing]);

  useEffect(() => {
    if (isEditing) {
      setIsDirty(editedDSL !== dslText);
    }
  }, [editedDSL, dslText, isEditing]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dslText);
      setCopied(true);
      toast.success('DSL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([dslText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${machine.name || 'machine'}.dsl`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('DSL file downloaded');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedDSL(dslText);
  };

  const handleSave = async () => {
    if (!onDSLEdit) {
      toast.error('DSL editing not configured');
      return;
    }

    try {
      await onDSLEdit(editedDSL);
      setIsEditing(false);
      setIsDirty(false);
      toast.success('DSL changes applied');
    } catch (error) {
      toast.error('Failed to apply DSL changes');
    }
  };

  const handleCancel = () => {
    setEditedDSL(dslText);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleRegenerate = () => {
    const generated = generateDSL(machine);
    setDslText(generated);
    setEditedDSL(generated);
    toast.success('DSL regenerated from machine');
  };

  return (
    <div className="h-full flex flex-col" data-testid="dsl-viewer">
      {/* Header */}
      <div className="p-4 border-b border-white/10 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold">DSL Representation</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEditing ? 'Editing mode - changes not yet applied' : 'Read-only view synced with graph'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isDirty && (
              <Badge variant="outline" className="text-[10px] h-5 text-yellow-400 border-yellow-500/30">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Modified
              </Badge>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRegenerate}
              className="h-7"
              title="Regenerate from machine"
              data-testid="regenerate-dsl"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <>
              <Button
                size="sm"
                onClick={handleEdit}
                className="h-7 text-xs"
                data-testid="edit-dsl-button"
              >
                Edit DSL
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopy}
                className="h-7 text-xs"
              >
                {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                className="h-7 text-xs"
              >
                <Download className="w-3 h-3 mr-1" />
                Download
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!isDirty}
                className="h-7 text-xs"
                data-testid="save-dsl-button"
              >
                Apply Changes
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                className="h-7 text-xs"
              >
                Cancel
              </Button>
            </>
          )}
        </div>

        {/* Info box */}
        <div className="text-xs text-gray-500 bg-blue-500/10 border border-blue-500/30 rounded p-2">
          <div className="flex items-start space-x-2">
            <div className="text-blue-400 mt-0.5">ℹ</div>
            <div>
              This DSL is automatically synchronized with graph changes. 
              Edit here to modify machine structure via text.
            </div>
          </div>
        </div>
      </div>

      {/* DSL Text */}
      <div className="flex-1 overflow-hidden p-4">
        {isEditing ? (
          <Textarea
            value={editedDSL}
            onChange={(e) => setEditedDSL(e.target.value)}
            className="w-full h-full font-mono text-xs bg-black/30 resize-none"
            placeholder="Enter DSL here..."
            data-testid="dsl-editor"
          />
        ) : (
          <pre className="w-full h-full p-3 bg-black/30 rounded border border-white/10 overflow-auto text-xs font-mono text-gray-300 whitespace-pre">
            {dslText}
          </pre>
        )}
      </div>
    </div>
  );
};

export default DSLViewer;
